import React, { useState, useEffect } from 'react';
import { registerAdmin } from '../socket';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from 'axios';
import logo from '../assets/logonew.png';

// ── Shared eye-toggle icons ──────────────────────────────────────────────────
const EyeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);
const EyeOffIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
  </svg>
);

// ── Step indicator ────────────────────────────────────────────────────────────
const StepDot = ({ active, done, label }) => (
  <div className="flex flex-col items-center gap-1">
    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all
      ${done ? 'bg-green-500 text-white' : active ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-400'}`}>
      {done ? (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      ) : label}
    </div>
  </div>
);

const StepBar = ({ step }) => (
  <div className="flex items-center gap-0 mb-6 w-full justify-center">
    <StepDot active={step === 1} done={step > 1} label="1" />
    <div className={`h-0.5 w-10 transition-all ${step > 1 ? 'bg-green-500' : 'bg-gray-200'}`} />
    <StepDot active={step === 2} done={step > 2} label="2" />
    <div className={`h-0.5 w-10 transition-all ${step > 2 ? 'bg-green-500' : 'bg-gray-200'}`} />
    <StepDot active={step === 3} done={false} label="3" />
  </div>
);

const Login = () => {
  // ── Login state ──
  const [isVisible, setIsVisible] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "", otp: "" });
  const [timer, setTimer] = useState(60);
  const [organizationName, setOrganizationName] = useState("");

  // ── Forgot-password state ──
  const [fpView, setFpView] = useState(false);       // show/hide the FP modal
  const [fpStep, setFpStep] = useState(1);           // 1=email, 2=otp, 3=new-pw
  const [fpEmail, setFpEmail]       = useState("");
  const [fpAdminId, setFpAdminId]   = useState(null);
  const [fpOtp, setFpOtp]           = useState("");
  const [fpOtpTimer, setFpOtpTimer] = useState(180);
  const [fpPassword, setFpPassword] = useState("");
  const [fpConfirm, setFpConfirm]   = useState("");
  const [fpShowPw, setFpShowPw]     = useState(false);
  const [fpShowCf, setFpShowCf]     = useState(false);
  const [fpLoading, setFpLoading]   = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Show reason message when redirected here by a forceLogout socket event
  useEffect(() => {
    const reason = searchParams.get('reason');
    if (reason) {
      toast.error(decodeURIComponent(reason), { autoClose: 8000 });
    }
  }, []);

  useEffect(() => {
    let interval = null;
    if (isVisible) {
      interval = setInterval(() => {
        setTimer((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);
    }
    if (timer === 0) {
      toast.error("OTP expired, please request a new one.");
      setIsVisible(false);
      setTimer(180);
    }
    return () => clearInterval(interval);
  }, [isVisible, timer]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/login`, formData);
      if (response.data.token) {
        const orgName = response.data.organization_name;
        sessionStorage.setItem("organizationName", orgName);
        sessionStorage.setItem(`${orgName}_token`, response.data.token);
        sessionStorage.setItem(`${orgName}_adminId`, response.data.adminId);
        sessionStorage.setItem(`${orgName}_organizationId`, response.data.organization_id);
        sessionStorage.setItem(`${orgName}_role`, response.data.role);
        sessionStorage.setItem(`${orgName}_features`, response.data.features || 'livechat,tickets');
        setOrganizationName(orgName);
        sendOtp(formData.email);
      } else {
        toast.error("Login failed. Check your credentials.");
      }
    }
    catch (error) {
      const status = error.response?.status;
      if (status === 401) {
        toast.error("Invalid credentials. Please try again.");
      } else if (status === 403) {
        toast.error("Your account is in pending status, please wait for admin approval.");
      } else if (status === 404) {
        toast.error("No account found with this email. Please check and try again.");
      } else {
        toast.error("Login failed. Please try again later.");
      }
    }
  };

  const sendOtp = async (email) => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/otp/sendOtp`, { email });
      setIsVisible(true);
      setTimer(180);
    } catch {
      toast.error("Failed to send OTP. Try again later.");
    }
  };

  const OTP_Length = 6;

  const handleOtpChange = (e) =>{
    const otpValue = e.target.value;
    console.log("OTP Value:", otpValue);
    
    setFormData({ ...formData, otp: otpValue });
    if (otpValue.length === OTP_Length) {
      
      setTimeout(()=>{handleVerifyOtpWithValue(otpValue);}, 600); // Delay for 1 second before verifying OTP
    }
  };

  const handleVerifyOtpWithValue = async (otpValue) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/otp/verifyOtp`, {
        email: formData.email,
        otp: otpValue, // Use the passed value, not from state
      });
      if (response.data.message === "OTP verified successfully.") {
        registerAdmin();
        navigate(`/${organizationName}/home/dashboard`);
      } else {
        toast.error("Invalid or expired OTP");
      }
    } catch {
      toast.error("OTP verification failed. Try again.");
    }
  };
  

  const handleVerifyOtp = async () => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/otp/verifyOtp`, {
        email: formData.email,
        otp: formData.otp,
      });
      if (response.data.message === "OTP verified successfully.") {
        registerAdmin();
        navigate(`/${organizationName}/home/dashboard`);
      } else {
        toast.error("Invalid or expired OTP");
      }
    } catch {
      toast.error("OTP verification failed. Try again.");
    }
  };

  // ── FP OTP countdown ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!fpView || fpStep !== 2) return;
    if (fpOtpTimer <= 0) {
      toast.error("OTP expired. Please request a new one.");
      setFpStep(1);
      setFpOtp("");
      setFpOtpTimer(180);
      return;
    }
    const id = setInterval(() => setFpOtpTimer((t) => t - 1), 1000);
    return () => clearInterval(id);
  }, [fpView, fpStep, fpOtpTimer]);

  // ── FP Step 1: verify email & send OTP ───────────────────────────────────
  const handleFpCheckEmail = async () => {
    if (!fpEmail.trim()) return toast.error("Please enter your email address.");
    setFpLoading(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/forgotPassword/checkEmail`, { email: fpEmail });
      setFpAdminId(res.data.adminId);
      setFpStep(2);
      setFpOtpTimer(180);
      toast.success("OTP sent! Check your inbox.");
    } catch (err) {
      toast.error(err.response?.data?.error || "Email not found. Please check and try again.");
    } finally {
      setFpLoading(false);
    }
  };

  // ── FP Step 2: verify OTP ─────────────────────────────────────────────────
  const handleFpVerifyOtp = async (otpValue) => {
    const code = otpValue ?? fpOtp;
    if (code.length < 6) return;
    setFpLoading(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/otp/verifyOtp`, { email: fpEmail, otp: code });
      if (res.data.message === "OTP verified successfully.") {
        setFpStep(3);
      } else {
        toast.error("Invalid OTP. Please try again.");
      }
    } catch {
      toast.error("OTP verification failed. Try again.");
    } finally {
      setFpLoading(false);
    }
  };

  const handleFpOtpChange = (e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 6);
    setFpOtp(val);
    if (val.length === 6) setTimeout(() => handleFpVerifyOtp(val), 400);
  };

  // ── FP Step 3: reset password ─────────────────────────────────────────────
  const handleFpReset = async () => {
    if (!fpPassword) return toast.error("Please enter a new password.");
    if (fpPassword !== fpConfirm) return toast.error("Passwords do not match.");
    setFpLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/forgotPassword/reset`, {
        adminId: fpAdminId,
        password: fpPassword,
      });
      toast.success("Password reset successfully! You can now sign in.");
      // Reset FP state and go back to login
      setFpView(false);
      setFpStep(1);
      setFpEmail("");
      setFpOtp("");
      setFpPassword("");
      setFpConfirm("");
      setFpAdminId(null);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to reset password. Please try again.");
    } finally {
      setFpLoading(false);
    }
  };

  const closeFp = () => {
    setFpView(false);
    setFpStep(1);
    setFpEmail("");
    setFpOtp("");
    setFpPassword("");
    setFpConfirm("");
    setFpAdminId(null);
    setFpOtpTimer(180);
  };

  return (
    <section className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-500 via-purple-600 to-red-400">
      <ToastContainer autoClose={5000} pauseOnHover />

      {/* ── Login card ─────────────────────────────────────────────────────── */}
      <div className="bg-white shadow-lg rounded-xl p-8 max-w-md w-full">
        <img src={logo} alt="logo" className="w-20 h-20 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-gray-800 text-center">Sign in to your account</h1>
        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <input
            type="email" name="email" placeholder="Email" required
            className="w-full px-4 py-2 border rounded-lg focus:ring focus:border-blue-500"
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          />
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"} name="password" placeholder="Password" required
              className="w-full px-4 py-2 border rounded-lg focus:ring focus:border-blue-500 pr-10"
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            />
            <button type="button" onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700">
              {showPassword ? <EyeOffIcon /> : <EyeIcon />}
            </button>
          </div>

          {/* Forgot password link */}
          {!isVisible && (
            <div className="text-right -mt-1">
              <button
                type="button"
                onClick={() => setFpView(true)}
                className="text-sm text-indigo-500 hover:underline font-medium"
              >
                Forgot password?
              </button>
            </div>
          )}

          {isVisible && (
            <div className="space-y-2">
              <input
                type="text" name="otp" onChange={handleOtpChange} maxLength={OTP_Length}
                placeholder="Enter OTP"
                className="w-full px-4 py-2 border rounded-lg focus:ring focus:border-blue-500 tracking-widest text-center text-lg font-mono"
                required
              />
              <p className="text-sm text-red-600">Time remaining: {timer}s</p>
              <button type="button" onClick={handleVerifyOtp}
                className="w-full bg-green-500 text-white py-2 rounded-lg hover:bg-green-600 transition font-semibold">
                Verify OTP
              </button>
            </div>
          )}

          {!isVisible && (
            <button type="submit"
              className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold py-3 rounded-lg shadow-md hover:shadow-xl transition">
              Sign in
            </button>
          )}
        </form>

        <p className="text-center text-gray-600 mt-4">
          Don't have an account?{" "}
          <a href="/signup" className="text-indigo-500 font-semibold hover:underline">Register</a>
        </p>
      </div>

      {/* ── Forgot Password Modal ──────────────────────────────────────────── */}
      {fpView && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)' }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-7 relative">

            {/* Close */}
            <button onClick={closeFp}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <h2 className="text-xl font-bold text-gray-800 mb-1 text-center">Reset Password</h2>
            <p className="text-sm text-gray-500 text-center mb-5">
              {fpStep === 1 && "Enter your account email to receive a verification code."}
              {fpStep === 2 && `We sent a 6-digit code to ${fpEmail}`}
              {fpStep === 3 && "Choose a strong new password."}
            </p>

            <StepBar step={fpStep} />

            {/* Step 1 — Email */}
            {fpStep === 1 && (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={fpEmail}
                    onChange={(e) => setFpEmail(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleFpCheckEmail()}
                    placeholder="you@example.com"
                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 transition"
                  />
                </div>
                <button
                  onClick={handleFpCheckEmail}
                  disabled={fpLoading || !fpEmail.trim()}
                  className="w-full flex items-center justify-center gap-2 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-sm rounded-xl transition"
                >
                  {fpLoading && (
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                    </svg>
                  )}
                  {fpLoading ? "Sending…" : "Send Verification Code"}
                </button>
              </div>
            )}

            {/* Step 2 — OTP */}
            {fpStep === 2 && (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                    6-digit Code
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={fpOtp}
                    onChange={handleFpOtpChange}
                    maxLength={6}
                    placeholder="——————"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-2xl tracking-[0.4em] font-mono text-center text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 transition"
                  />
                  <p className={`text-xs mt-1.5 text-center font-medium ${fpOtpTimer <= 30 ? 'text-red-500' : 'text-gray-400'}`}>
                    Code expires in {Math.floor(fpOtpTimer / 60)}:{String(fpOtpTimer % 60).padStart(2, '0')}
                  </p>
                </div>
                <button
                  onClick={() => handleFpVerifyOtp()}
                  disabled={fpLoading || fpOtp.length < 6}
                  className="w-full flex items-center justify-center gap-2 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-sm rounded-xl transition"
                >
                  {fpLoading && (
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                    </svg>
                  )}
                  {fpLoading ? "Verifying…" : "Verify Code"}
                </button>
                <button
                  type="button"
                  disabled={fpLoading}
                  onClick={() => { setFpStep(1); setFpOtp(""); }}
                  className="w-full text-sm text-indigo-500 hover:underline text-center disabled:opacity-40"
                >
                  ← Use a different email
                </button>
              </div>
            )}

            {/* Step 3 — New password */}
            {fpStep === 3 && (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      type={fpShowPw ? "text" : "password"}
                      value={fpPassword}
                      onChange={(e) => setFpPassword(e.target.value)}
                      placeholder="Enter new password"
                      className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800 pr-10 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 transition"
                    />
                    <button type="button" onClick={() => setFpShowPw((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {fpShowPw ? <EyeOffIcon /> : <EyeIcon />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      type={fpShowCf ? "text" : "password"}
                      value={fpConfirm}
                      onChange={(e) => setFpConfirm(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleFpReset()}
                      placeholder="Re-enter new password"
                      className={`w-full px-4 py-2.5 bg-gray-50 border rounded-xl text-sm text-gray-800 pr-10 focus:outline-none focus:ring-2 transition
                        ${fpConfirm && fpPassword !== fpConfirm
                          ? 'border-red-300 focus:ring-red-200'
                          : fpConfirm && fpPassword === fpConfirm
                          ? 'border-green-300 focus:ring-green-200'
                          : 'border-gray-200 focus:ring-indigo-300'}`}
                    />
                    <button type="button" onClick={() => setFpShowCf((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {fpShowCf ? <EyeOffIcon /> : <EyeIcon />}
                    </button>
                  </div>
                  {fpConfirm && fpPassword !== fpConfirm && (
                    <p className="text-xs text-red-500 mt-1">Passwords do not match.</p>
                  )}
                  {fpConfirm && fpPassword === fpConfirm && (
                    <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>
                      Passwords match
                    </p>
                  )}
                </div>
                <button
                  onClick={handleFpReset}
                  disabled={fpLoading || !fpPassword || fpPassword !== fpConfirm}
                  className="w-full flex items-center justify-center gap-2 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-sm rounded-xl transition"
                >
                  {fpLoading && (
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                    </svg>
                  )}
                  {fpLoading ? "Resetting…" : "Reset Password"}
                </button>
              </div>
            )}

          </div>
        </div>
      )}
    </section>
  );
};

export default Login;
